package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.FeatureFlagsContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.api.dto.admin.FeatureFlagDto
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/feature-flags")
@Composable
fun FeatureFlagsPage() {
    AdminPageScaffold(title = "Feature Flags", currentPath = "/feature-flags", showTitle = false) {
        val container = koinContainer<FeatureFlagsContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is FeatureFlagsAction.Navigate -> navigate(action.path)
                }
            }
            FeatureFlagsContent(state, onIntent = ::intent)
        }
    }
}

@Composable
private fun FeatureFlagsContent(state: FeatureFlagsState, onIntent: (FeatureFlagsIntent) -> Unit) {
    AdminPageHeader(
        title = "Feature Flags",
        subtitle = "Toggle features at runtime — no redeploy",
        actions = {
            AdminButton(
                label = "Refresh",
                onClick = { onIntent(FeatureFlagsIntent.Refresh) },
                variant = AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        },
    )

    when (val data = state.flags) {
        AsyncSection.Loading -> AdminPanel(title = "Flags") { AdminSkeletonTable(rows = 2) }
        is AsyncSection.Failed -> AdminEmptyState(
            title = "Failed to load",
            description = data.message,
            actionLabel = "Retry",
            onAction = { onIntent(FeatureFlagsIntent.Refresh) },
        )
        is AsyncSection.Loaded -> {
            if (data.value.isEmpty()) {
                AdminEmptyState(title = "No feature flags")
            } else {
                AdminPanel(title = "Flags") {
                    data.value.forEach { flag ->
                        FeatureFlagRow(flag = flag, saving = flag.key in state.pendingKeys, onIntent = onIntent)
                    }
                }
            }
        }
    }
}

@Composable
private fun FeatureFlagRow(flag: FeatureFlagDto, saving: Boolean, onIntent: (FeatureFlagsIntent) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(HandpassSpacing.MD)
            padding(HandpassSpacing.MD, 0.px)
            property("border-bottom", "1px solid ${Border.value()}")
        }
    }) {
        Div {
            Div {
                Span({ style { fontWeight(600); fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                    Text(flag.label)
                }
            }
            flag.description?.let { desc ->
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                    Text(desc)
                }
            }
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(HandpassSpacing.SM)
                property("flex-shrink", "0")
            }
        }) {
            AdminBadge(
                label = if (flag.enabled) "Enabled" else "Disabled",
                variant = if (flag.enabled) AdminBadgeVariant.Success else AdminBadgeVariant.Neutral,
            )
            AdminButton(
                label = if (flag.enabled) "Turn off" else "Turn on",
                onClick = { onIntent(FeatureFlagsIntent.Toggle(flag.key, !flag.enabled)) },
                variant = if (flag.enabled) AdminButtonVariant.Destructive else AdminButtonVariant.Primary,
                size = AdminButtonSize.SM,
                enabled = !saving,
                loading = saving,
            )
        }
    }
}
